import { ReactNode } from "react";
import { Check } from "lucide-react";
import { Label } from "@/components/ui/label";
import { HelpIcon } from "@/components/shared/HelpIcon";
import { cn } from "@/lib/utils";
import { ErrorTextComponent } from "../error-text-component";
import { HelperTextComponent } from "../helper-text-component";

export interface ChoiceCardOption<T extends string | number = string> {
  value: T;
  /** Texto principal — deve descrever a consequência da escolha, não o termo técnico. */
  title: ReactNode;
  /** Linha de apoio sempre visível. Reforça o que vai acontecer ao escolher. */
  description?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

interface ChoiceCardsProps<T extends string | number = string> {
  options: ChoiceCardOption<T>[];
  value?: T;
  onChange?: (value: T) => void;
  label?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  tooltip?: string;
  tooltipHtml?: ReactNode;
  /** Número de colunas em telas md+. Padrão: 2. */
  columns?: 1 | 2 | 3 | 4;
  className?: string;
  name?: string;
}

const columnsClass: Record<NonNullable<ChoiceCardsProps["columns"]>, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  // 4 é para escolha curta e sem descrição (periodicidade) — em duas colunas no tablet, para o
  // cartão não virar uma tira de 90px.
  4: "sm:grid-cols-2 md:grid-cols-4",
};

/**
 * Escolha por cartões selecionáveis (radio cards).
 *
 * Alternativa ao <select> para decisões conceituais: em vez de esconder as
 * opções e suas explicações até o usuário abrir o dropdown, todas ficam
 * visíveis de uma vez. O título carrega a decisão (a consequência), de modo
 * que o usuário entende mesmo sem ler a descrição — pensado para usuários que
 * não exploram tooltips nem leem texto auxiliar.
 */
export function ChoiceCards<T extends string | number = string>({
  options,
  value,
  onChange,
  label,
  required,
  disabled,
  error,
  helperText,
  tooltip,
  tooltipHtml,
  columns = 2,
  className,
  name,
}: ChoiceCardsProps<T>) {
  return (
    <div className={cn("w-full space-y-2", className)}>
      {label && (
        <div className="flex min-h-[20px] items-center gap-2">
          <Label className={cn("text-base font-medium md:text-sm", error && "text-destructive")}>
            {required && <span className="text-destructive">* </span>}
            {label}
          </Label>
          {(tooltip || tooltipHtml) && (
            <HelpIcon tooltipMessage={tooltip} tooltipHtml={tooltipHtml} />
          )}
        </div>
      )}

      <div
        role="radiogroup"
        aria-label={typeof label === "string" ? label : name}
        className={cn("grid grid-cols-1 gap-3", columnsClass[columns])}
      >
        {options.map((option) => {
          const isSelected = String(option.value) === String(value);
          const isDisabled = disabled || option.disabled;

          return (
            <button
              key={String(option.value)}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={isDisabled}
              onClick={() => !isDisabled && onChange?.(option.value)}
              className={cn(
                "relative flex items-start gap-3 rounded-lg border p-4 text-left transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isSelected
                  ? "border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20"
                  : "border-border bg-card hover:border-primary/40 hover:bg-muted/30",
                isDisabled && "cursor-not-allowed opacity-50 hover:border-border hover:bg-card",
              )}
            >
              {option.icon && (
                <span
                  className={cn(
                    "mt-0.5 shrink-0",
                    isSelected ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  {option.icon}
                </span>
              )}

              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-sm font-medium leading-tight">{option.title}</span>
                {option.description && (
                  <span className="text-xs leading-snug text-muted-foreground">
                    {option.description}
                  </span>
                )}
              </span>

              <span
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors",
                  isSelected
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border",
                )}
                aria-hidden
              >
                {isSelected && <Check className="size-3.5" />}
              </span>
            </button>
          );
        })}
      </div>

      <ErrorTextComponent error={error ?? ""} />
      <HelperTextComponent text={helperText ?? ""} error={error} />
    </div>
  );
}
